import type { ICallDetails } from '../../../../../../../../../../models/call-details.model';
import LineMarker from '../../../LineMarker';
import { CallDetailsHelper, DepartureOrArrivalType } from '../../../../../../../../../../helpers/call-details.helper';
import { LineHelper } from '../../../../../../../../../../helpers/line.helper';
import Colors from '../../../../../../../../../../helpers/colors.helper';
import { ITripLeg } from '../../../../../../../../../../models/trip-leg.model';
import EstimatedAndPlannedTimes from '../../../shared/EstimatedAndPlannedTimes';
import { CircleDisplayType, LineDistance, LineType } from '../../../LineMarker/line-markers.enum';
import RealTimeAndPlannedPlatforms from '../../../shared/RealTimeAndPlannedPlatforms';

function Call({
  callsBeforeTripLeg,
  callsOnTripLeg,
  callsAfterTripLeg,
  tripLeg,
  call,
  callIndex,
  isDestination,
  isOnTripLeg,
  showCallsBeforeTripLeg,
  showCallsAfterTripLeg,
  extendsFromCall,
  extendsToCall,
}: {
  callsBeforeTripLeg: ICallDetails[];
  callsOnTripLeg: ICallDetails[];
  callsAfterTripLeg: ICallDetails[];
  tripLeg: ITripLeg;
  call: ICallDetails;
  callIndex: number;
  isDestination: boolean;
  isOnTripLeg: boolean;
  showCallsBeforeTripLeg: boolean;
  showCallsAfterTripLeg: boolean;
  extendsFromCall: boolean;
  extendsToCall: boolean;
}) {
  const totalNumberOfCalls = callsBeforeTripLeg.length + callsOnTripLeg.length + callsAfterTripLeg.length;
  const isCallOnTime = CallDetailsHelper.isOnTime(call, isDestination ? DepartureOrArrivalType.arrival : DepartureOrArrivalType.departure);
  const isLastCall = callIndex === totalNumberOfCalls - (!showCallsAfterTripLeg ? callsAfterTripLeg.length : 0) - 1;
  const isLastCallOnServiceJourney = callIndex === totalNumberOfCalls - 1;

  const estimatedTime = isCallOnTime ? undefined : isDestination || isLastCall ? call.estimatedArrivalTime : call.estimatedDepartureTime;
  const plannedTime = isDestination || isLastCall ? call.plannedArrivalTime : call.plannedDepartureTime;
  const lineForegroundColor = call.isOnTripLeg ? (LineHelper.getBackgroundSafeForegroundColor(tripLeg.serviceJourney.line) ?? Colors.vtdWhite) : Colors.vtdGray400;
  const lineBackgroundColor = call.isOnTripLeg ? (LineHelper.getBackgroundSafeBackgroundColor(tripLeg.serviceJourney.line) ?? Colors.vtdGray700) : Colors.vtdGray400;

  let lineDistance = callIndex === 0 ? LineDistance.halfEnd : callIndex === totalNumberOfCalls - 1 ? LineDistance.halfStart : LineDistance.full;

  if (extendsFromCall) {
    lineDistance = LineDistance.halfEnd;
  } else if (extendsToCall) {
    lineDistance = LineDistance.halfStart;
  }

  return (
    <li key={call.stopPoint.gid} className="d-flex overflow-hidden print-no-break">
      <LineMarker
        foregroundColor={lineForegroundColor}
        backgroundColor={lineBackgroundColor}
        lineColor={lineForegroundColor}
        lineType={LineType.solid}
        circleDisplayType={CircleDisplayType.center}
        secondaryLineColor={extendsFromCall || extendsToCall ? Colors.vtdGray400 : undefined}
        fadeSecondaryLineColor={(extendsFromCall && !showCallsBeforeTripLeg) || (extendsToCall && !showCallsAfterTripLeg)}
        lineDistance={lineDistance}
        className="flex-shrink-0 trip-leg-calls__line-marker"
      />
      <div className={`flex-grow-1 ms-2 py-2 ${isLastCall ? '' : 'border-bottom'}`}>
        <EstimatedAndPlannedTimes
          estimatedTime={estimatedTime}
          plannedTime={plannedTime}
          isCancelled={call.isCancelled}
          isArrivalCancelled={isLastCallOnServiceJourney || callIndex === 0 ? undefined : call.isArrivalCancelled}
          isDepartureCancelled={isLastCallOnServiceJourney || callIndex === 0 ? undefined : call.isDepartureCancelled}
        />
        <div className="d-flex justify-content-between">
          <div className={isOnTripLeg ? 'fw-bold' : ''}>{call.stopPoint.name}</div>
          <div className="ms-2 flex-shrink-0">
            <RealTimeAndPlannedPlatforms stopPoint={call.stopPoint} realtimeStopPoint={call.realtimeStopPoint} className="text-color-gray-800" />
          </div>
        </div>
      </div>
    </li>
  );
}

export default Call;
